import React from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ScoreDisplay } from '../components/ScoreDisplay';
import { students } from '../lib/mockData';
import { ArrowLeft, Calendar, Mail, Printer, TrendingUp, AlertTriangle, CheckCircle2 } from 'lucide-react';
interface StudentProfileProps {
  studentId: string;
  onBack: () => void;
}
export function StudentProfile({
  studentId,
  onBack
}: StudentProfileProps) {
  const student = students.find(s => s.id === studentId) || students[0];
  return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-301">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8" data-id="element-302">
        <button onClick={onBack} className="p-2 hover:bg-stone-100 rounded-full text-stone-500 transition-colors" data-id="element-303">
          <ArrowLeft size={24} data-id="element-304" />
        </button>
        <div data-id="element-305">
          <h1 className="text-2xl font-bold text-stone-900" data-id="element-306">
            {student.firstName} {student.lastName}
          </h1>
          <div className="flex items-center gap-2 text-stone-500 text-sm mt-1" data-id="element-307">
            <span data-id="element-308">{student.grade}</span>
            <span data-id="element-309">•</span>
            <span data-id="element-310">{student.school}</span>
            <span data-id="element-311">•</span>
            <span data-id="element-312">ID: #{student.id.padStart(6, '0')}</span>
          </div>
        </div>
        <div className="ml-auto flex gap-3" data-id="element-313">
          <Button variant="outline" icon={<Printer size={16} data-id="element-315" />} data-id="element-314">
            Print Profile
          </Button>
          <Button icon={<Mail size={16} data-id="element-317" />} data-id="element-316">Generate Letter</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-318">
        {/* Main Column: Literacy & Attendance */}
        <div className="lg:col-span-2 space-y-6" data-id="element-319">
          {/* Literacy Card */}
          <Card className="overflow-hidden" data-id="element-320">
            <div className="flex items-center justify-between mb-6" data-id="element-321">
              <div className="flex items-center gap-2" data-id="element-322">
                <div className="p-2 bg-teal-50 text-teal-600 rounded-lg" data-id="element-323">
                  <TrendingUp size={20} data-id="element-324" />
                </div>
                <h2 className="text-lg font-semibold text-stone-900" data-id="element-325">
                  Literacy Progress
                </h2>
              </div>
              <div className="text-sm text-stone-500" data-id="element-326">
                Next Assessment:{' '}
                <span className="font-medium text-stone-900" data-id="element-327">
                  {student.nextTestDate}
                </span>
              </div>
            </div>

            <div className="bg-stone-50 rounded-xl p-6 mb-8" data-id="element-328">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-329">
                <div data-id="element-330">
                  <ScoreDisplay score={student.literacyScore} level={student.literacyLevel} isBeginningReader={student.isBeginningReader} data-id="element-331" />
                  <p className="mt-4 text-stone-600 text-sm leading-relaxed" data-id="element-332">
                    {student.literacyLevel === 'On Track' ? `${student.firstName} is reading at grade level! Their score indicates they can handle ${student.grade} text complexity.` : `${student.firstName} would benefit from additional reading practice. Their current level suggests focusing on foundational skills.`}
                  </p>
                </div>

                {/* Simple History Chart Visualization */}
                <div className="h-40 flex items-end justify-between gap-2 px-4 pb-2 border-b border-stone-200" data-id="element-333">
                  {student.history.map((point, i) => {
                  const height = Math.max(20, point.score / 1400 * 100);
                  return <div key={i} className="flex flex-col items-center gap-2 w-1/3 group" data-id="element-334">
                        <div className="relative w-full flex justify-center" data-id="element-335">
                          <div className={`w-12 rounded-t-lg transition-all duration-500 ${i === student.history.length - 1 ? 'bg-teal-500' : 'bg-teal-200'}`} style={{
                        height: `${height}%`
                      }} data-id="element-336"></div>
                          <div className="absolute -top-8 bg-stone-800 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-337">
                            {point.score}L
                          </div>
                        </div>
                        <span className="text-xs text-stone-500 font-medium" data-id="element-338">
                          {point.date.split('-')[1]}/{point.date.split('-')[2]}
                        </span>
                      </div>;
                })}
                </div>
              </div>
            </div>

            <h3 className="font-medium text-stone-900 mb-4" data-id="element-339">Letter History</h3>
            <div className="space-y-3" data-id="element-340">
              {[1, 2].map(i => <div key={i} className="flex items-center justify-between p-3 border border-stone-100 rounded-lg hover:bg-stone-50 transition-colors" data-id="element-341">
                  <div className="flex items-center gap-3" data-id="element-342">
                    <div className="w-10 h-10 bg-white border border-stone-200 rounded flex items-center justify-center text-stone-400" data-id="element-343">
                      <Mail size={20} data-id="element-344" />
                    </div>
                    <div data-id="element-345">
                      <div className="font-medium text-stone-900" data-id="element-346">
                        Fall 2023 Literacy Update
                      </div>
                      <div className="text-xs text-stone-500" data-id="element-347">
                        Sent Oct 30, 2023
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" data-id="element-348">
                    View
                  </Button>
                </div>)}
            </div>
          </Card>

          {/* Attendance Card */}
          <Card data-id="element-349">
            <div className="flex items-center gap-2 mb-6" data-id="element-350">
              <div className="p-2 bg-coral-50 text-coral-600 rounded-lg" data-id="element-351">
                <Calendar size={20} data-id="element-352" />
              </div>
              <h2 className="text-lg font-semibold text-stone-900" data-id="element-353">
                Attendance Summary
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-354">
              <div className="p-4 bg-stone-50 rounded-xl text-center" data-id="element-355">
                <div className="text-sm text-stone-500 mb-1" data-id="element-356">
                  Attendance Rate
                </div>
                <div className={`text-3xl font-bold ${student.attendanceRate >= 90 ? 'text-teal-600' : 'text-coral-600'}`} data-id="element-357">
                  {student.attendanceRate}%
                </div>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl text-center" data-id="element-358">
                <div className="text-sm text-stone-500 mb-1" data-id="element-359">Days Absent</div>
                <div className="text-3xl font-bold text-stone-900" data-id="element-360">
                  {student.absences}
                </div>
              </div>
              <div className="p-4 bg-stone-50 rounded-xl text-center" data-id="element-361">
                <div className="text-sm text-stone-500 mb-1" data-id="element-362">Risk Level</div>
                <Badge variant={student.attendanceRate >= 90 ? 'success' : 'danger'} data-id="element-363">
                  {student.attendanceRate >= 90 ? 'Low Risk' : 'Chronic Absence'}
                </Badge>
              </div>
            </div>

            {student.attendanceRate < 90 && <div className="mt-6 p-4 bg-coral-50 border border-coral-100 rounded-lg flex gap-3" data-id="element-364">
                <AlertTriangle className="text-coral-600 flex-shrink-0" size={20} data-id="element-365" />
                <div data-id="element-366">
                  <h4 className="font-semibold text-coral-900 text-sm" data-id="element-367">
                    Attendance Impact
                  </h4>
                  <p className="text-sm text-coral-800 mt-1" data-id="element-368">
                    {student.firstName} has missed {student.absences} days.
                    Students with this attendance pattern are 20% less likely to
                    meet end-of-year reading goals.
                  </p>
                </div>
              </div>}
          </Card>
        </div>

        {/* Sidebar Column: Family Info & Notes */}
        <div className="space-y-6" data-id="element-369">
          <Card data-id="element-370">
            <h3 className="font-semibold text-stone-900 mb-4" data-id="element-371">
              Family Contact
            </h3>
            <div className="space-y-4" data-id="element-372">
              <div data-id="element-373">
                <div className="text-xs text-stone-500 uppercase tracking-wide font-medium mb-1" data-id="element-374">
                  Primary Guardian
                </div>
                <div className="font-medium text-stone-900" data-id="element-375">Sarah Johnson</div>
                <div className="text-sm text-stone-600" data-id="element-376">Mother</div>
              </div>
              <div data-id="element-377">
                <div className="text-xs text-stone-500 uppercase tracking-wide font-medium mb-1" data-id="element-378">
                  Preferred Language
                </div>
                <Badge variant="neutral" data-id="element-379">English</Badge>
              </div>
              <div data-id="element-380">
                <div className="text-xs text-stone-500 uppercase tracking-wide font-medium mb-1" data-id="element-381">
                  Contact
                </div>
                <div className="text-sm text-stone-600" data-id="element-382">
                  sarah.j@example.com
                </div>
                <div className="text-sm text-stone-600" data-id="element-383">(555) 123-4567</div>
              </div>
            </div>
          </Card>

          <Card data-id="element-384">
            <h3 className="font-semibold text-stone-900 mb-4" data-id="element-385">Interventions</h3>
            <div className="space-y-3" data-id="element-386">
              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg" data-id="element-387">
                <CheckCircle2 size={16} className="text-teal-600 mt-0.5" data-id="element-388" />
                <div className="text-sm" data-id="element-389">
                  <div className="font-medium text-stone-900" data-id="element-390">
                    Reading Group B
                  </div>
                  <div className="text-stone-500" data-id="element-391">Started Sept 15</div>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg" data-id="element-392">
                <CheckCircle2 size={16} className="text-teal-600 mt-0.5" data-id="element-393" />
                <div className="text-sm" data-id="element-394">
                  <div className="font-medium text-stone-900" data-id="element-395">
                    After School Tutoring
                  </div>
                  <div className="text-stone-500" data-id="element-396">Tues/Thurs</div>
                </div>
              </div>
              <Button variant="outline" size="sm" className="w-full mt-2" data-id="element-397">
                Add Intervention
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>;
}